import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaUpload, FaTrash, FaImage, FaStore, FaBriefcase, FaUserShield, FaExclamationTriangle, FaTruck, FaTags, FaFileAlt } from 'react-icons/fa';
import { FaInstagram, FaFacebook, FaTiktok, FaYoutube, FaXTwitter, FaPinterest, FaLinkedin } from 'react-icons/fa6';
import './StoreSettings.css';

const SECTIONS = [
  { key: 'branding', label: 'Branding', icon: <FaImage /> },
  { key: 'profile', label: 'Store Profile', icon: <FaStore /> },
  { key: 'business', label: 'Business', icon: <FaBriefcase /> },
  { key: 'dashboard', label: 'Dashboard Content', icon: <FaBriefcase /> },
  { key: 'shipping', label: 'Shipping', icon: <FaTruck /> },
  { key: 'discount', label: 'Promo Discount', icon: <FaTags /> },
  { key: 'flat_discount', label: 'Flat Discount', icon: <FaTags /> },
  { key: 'account', label: 'Account', icon: <FaUserShield /> },
  { key: 'policies', label: 'Policy Pages', icon: <FaFileAlt /> },
  { key: 'danger', label: 'Danger Zone', icon: <FaExclamationTriangle /> },
];

const PLATFORM_OPTIONS = [
  { key: 'instagram', label: 'Instagram', icon: <FaInstagram /> },
  { key: 'facebook', label: 'Facebook', icon: <FaFacebook /> },
  { key: 'tiktok', label: 'TikTok', icon: <FaTiktok /> },
  { key: 'youtube', label: 'YouTube', icon: <FaYoutube /> },
  { key: 'twitter', label: 'X (Twitter)', icon: <FaXTwitter /> },
  { key: 'pinterest', label: 'Pinterest', icon: <FaPinterest /> },
  { key: 'linkedin', label: 'LinkedIn', icon: <FaLinkedin /> },
];

const StoreSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // which section is currently saving
  const [uploadingKey, setUploadingKey] = useState(null);

  const [account, setAccount] = useState({ username: '', email: '' });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountSaving, setAccountSaving] = useState(false);

  const sectionRefs = {
    branding: useRef(null),
    profile: useRef(null),
    business: useRef(null),
    dashboard: useRef(null),
    shipping: useRef(null),
    discount: useRef(null),
    flat_discount: useRef(null),
    account: useRef(null),
    danger: useRef(null),
    policies: useRef(null),
  };

  const [pauseConfirmText, setPauseConfirmText] = useState('');
  const [pauseSaving, setPauseSaving] = useState(false);

  const handleTogglePauseStore = async () => {
    const isCurrentlyPaused = s('maintenance_mode_enabled') === '1';

    if (!isCurrentlyPaused) {
      const confirmed = window.confirm(
        'Pause your live store? Visitors will see a maintenance page instead of your storefront. You can resume anytime.'
      );
      if (!confirmed) return;
    }

    setPauseSaving(true);
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maintenance_mode_enabled: isCurrentlyPaused ? '0' : '1' })
      });
      const data = await res.json();
      if (data.success) {
        setField('maintenance_mode_enabled', isCurrentlyPaused ? '0' : '1');
        toast.success(isCurrentlyPaused ? 'Store is live again!' : 'Store paused — visitors now see the maintenance page.');
      } else {
        toast.error('Failed to update');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setPauseSaving(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadAccount();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`https://demo-site.creativepulse.pk/demo-api/public/get-settings.php?t=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      if (data.success) setSettings(data.settings || {});
    } catch (e) {
      console.error(e);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadAccount = async () => {
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/get-admin-account.php');
      const data = await res.json();
      if (data.success) {
        setAccount({ username: data.admin.username, email: data.admin.email });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAccountLoading(false);
    }
  };

  const s = (key, fallback = '') => settings[key] ?? fallback;
  const setField = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const isOn = (key) => settings[key] === '1';
  const toggleField = (key) => setField(key, isOn(key) ? '0' : '1');

  const scrollToSection = (key) => {
    sectionRefs[key].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveFields = async (sectionKey, fields, successMessage) => {
    setSaving(sectionKey);
    try {
      const payload = {};
      fields.forEach(key => { payload[key] = settings[key] ?? ''; });

      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(successMessage);
      } else {
        toast.error('Failed to save');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setSaving(null);
    }
  };

  const handleLogoUpload = async (e, endpoint, settingKey) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size should be less than 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('image', file);
    setUploadingKey(settingKey);
    try {
      const res = await fetch(`https://demo-site.creativepulse.pk/demo-api/admin/${endpoint}`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setField(settingKey, data.image_url);
        toast.success('Uploaded successfully!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  const handleAccountSave = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      toast.error('Enter your current password to save any account changes');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    setAccountSaving(true);
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/update-admin-account.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: account.username,
          email: account.email,
          current_password: currentPassword,
          new_password: newPassword,
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Account updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(data.message || 'Failed to update account');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setAccountSaving(false);
    }
  };

  const [contactDraft, setContactDraft] = useState(null);

  const startContactEdit = () => {
    let socialLinks = [];
    if (s('social_links')) {
      try { socialLinks = JSON.parse(s('social_links')); } catch (e) { console.error(e); }
    } else {
      // One-time migration from the old fixed fields, if present
      const oldFields = [
        ['instagram', s('social_instagram')],
        ['facebook', s('social_facebook')],
        ['tiktok', s('social_tiktok')],
        ['youtube', s('social_youtube')],
      ];
      socialLinks = oldFields.filter(([, url]) => url).map(([platform, url]) => ({ platform, url }));
    }

    setContactDraft({
      store_whatsapp: s('store_whatsapp'), store_phone: s('store_phone'), store_email: s('store_email'),
      store_city: s('store_city'), store_address: s('store_address'),
      social_links: socialLinks,
    });
  };

  useEffect(() => { if (!loading) startContactEdit(); }, [loading]); // eslint-disable-line

  const saveContactSocial = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/save-contact-social.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        /*
        body: JSON.stringify(contactDraft)
        */
        body: JSON.stringify({
          ...contactDraft,
          social_links: JSON.stringify(contactDraft.social_links),
        })
      });
      const data = await res.json();
      if (data.success) { toast.success('Contact & social info saved'); loadSettings(); }
      else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const addSocialLink = () => {
    setContactDraft({
      ...contactDraft,
      social_links: [...contactDraft.social_links, { platform: 'instagram', url: '' }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updated = [...contactDraft.social_links];
    if (field === 'url' && value && !/^https?:\/\//i.test(value)) {
      // Auto-add https:// on blur-worthy input, but only once a real
      // domain-looking value exists — avoids prepending it while they're
      // still mid-typing "inst..." etc. Simplify to always store raw here
      // and let Footer.jsx's normalizeUrl handle display-time correctness
      // instead, since that's simpler and equally safe. See note below.
    }
    updated[index] = { ...updated[index], [field]: value };
    setContactDraft({ ...contactDraft, social_links: updated });
  };

  const removeSocialLink = (index) => {
    setContactDraft({
      ...contactDraft,
      social_links: contactDraft.social_links.filter((_, i) => i !== index)
    });
  };

  const PLATFORM_COLORS = {
    instagram: '#E1306C',
    facebook: '#1877F2',
    tiktok: '#010101',
    youtube: '#FF0000',
    twitter: '#000000',
    pinterest: '#E60023',
    linkedin: '#0A66C2',
  };

  const [activePolicyTab, setActivePolicyTab] = useState('shipping');
  const [policyDraft, setPolicyDraft] = useState(null);

  useEffect(() => {
    if (!loading) setPolicyDraft({
      shipping: s('policy_shipping_text'), returns: s('policy_returns_text'),
      privacy: s('policy_privacy_text'), terms: s('policy_terms_text'),
    });
    // eslint-disable-next-line
  }, [loading]);

  const savePolicies = async () => {
    setSaving('policies');
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/save-policies.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_shipping_text: policyDraft.shipping, policy_returns_text: policyDraft.returns,
          policy_privacy_text: policyDraft.privacy, policy_terms_text: policyDraft.terms,
        })
      });
      const data = await res.json();
      if (data.success) toast.success('Policies saved'); else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(null); }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="ss-page">
      <div className="ss-header">
        <h1>Store Settings</h1>
        <p>Manage your branding, store profile, business details, and account.</p>
      </div>

      <div className="ss-quicknav">
        {SECTIONS.map(sec => (
          <button key={sec.key} className="ss-quicknav-btn" onClick={() => scrollToSection(sec.key)}>
            {sec.icon} {sec.label}
          </button>
        ))}
      </div>

      {/* ===== BRANDING ===== */}
      <div className="ss-card" ref={sectionRefs.branding}>
        <h2>Branding</h2>
        <p className="ss-sub">Your store logo shown in the navbar and footer.</p>

        <div className="ss-branding-grid">
          <div className="ss-form-group">
            <label>Store Logo (Navbar)</label>
            <div className="ss-upload-area">
              {s('logo') && (
                <div className="ss-logo-preview">
                  <img src={s('logo')} alt="Store logo" />
                  <button type="button" onClick={() => setField('logo', '')}><FaTrash /></button>
                </div>
              )}
              <label className="ss-upload-btn">
                <FaUpload /> {uploadingKey === 'logo' ? 'Uploading...' : 'Upload Logo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, 'upload-logo.php', 'logo')} disabled={uploadingKey === 'logo'} />
              </label>
              <small className="sf-dim-hint">
                Recommended size: approx. 500×123px.
              </small>
            </div>
          </div>

          <div className="ss-form-group">
            <label>Footer Logo</label>
            <div className="ss-upload-area">
              {s('footer_logo') && (
                <div className="ss-logo-preview">
                  <img src={s('footer_logo')} alt="Footer logo" />
                  <button type="button" onClick={() => setField('footer_logo', '')}><FaTrash /></button>
                </div>
              )}
              <label className="ss-upload-btn">
                <FaUpload /> {uploadingKey === 'footer_logo' ? 'Uploading...' : 'Upload Footer Logo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, 'upload-footer-logo.php', 'footer_logo')} disabled={uploadingKey === 'footer_logo'} />
              </label>
              <small className="sf-dim-hint">
                Recommended size: approx. 500×123px.
              </small>
            </div>
          </div>
        </div>
        <small className="ss-hint">Logo uploads save immediately — no separate Save button needed here.</small>
      </div>

      {/* ===== STORE PROFILE ===== */}
      <div className="ss-card" ref={sectionRefs.profile}>
        <h2>Store Profile</h2>
        <p className="ss-sub">Basic information describing your store.</p>

        <div className="ss-form-group">
          <label>Store Name</label>
          <input type="text" value={s('store_name')} onChange={(e) => setField('store_name', e.target.value)} />
        </div>
        <div className="ss-form-group">
          <label>Tagline</label>
          <input type="text" value={s('store_tagline')} onChange={(e) => setField('store_tagline', e.target.value)} placeholder="A short line shown near your store name" />
        </div>
        <div className="ss-form-group">
          <label>Store Description</label>
          <textarea rows="3" value={s('store_description')} onChange={(e) => setField('store_description', e.target.value)} />
        </div>
        <div className="ss-form-group">
          <label>Store Category</label>
          <input type="text" value={s('store_category')} onChange={(e) => setField('store_category', e.target.value)} placeholder="e.g. Fashion & Apparel" />
        </div>

        <button
          className="ss-save-btn"
          disabled={saving === 'profile'}
          onClick={() => saveFields('profile', ['store_name', 'store_tagline', 'store_description', 'store_category'], 'Store profile saved!')}
        >
          {saving === 'profile' ? 'Saving...' : 'Save Store Profile'}
        </button>
      </div>

      {/* ===== BUSINESS ===== */}
      <div className="ss-card" ref={sectionRefs.business}>
        <h2>Business</h2>
        <p className="ss-sub">Contact and business details used across your storefront.</p>

        {contactDraft && (
          <section className="sf-card">
            <h2>Contact & Social Information</h2>
            <p className="sf-sub">Used across your storefront, including contact areas and the system-generated footer.</p>
            <h5 className="sf-subheading">Contact Details</h5>
            <div className="sf-form-row">
              <div className="form-group"><label>WhatsApp Number</label><input value={contactDraft.store_whatsapp} onChange={(e) => setContactDraft({ ...contactDraft, store_whatsapp: e.target.value })} /></div>
              <div className="form-group"><label>Phone Number</label><input value={contactDraft.store_phone} onChange={(e) => setContactDraft({ ...contactDraft, store_phone: e.target.value })} /></div>
            </div>
            <div className="sf-form-row">
              <div className="form-group"><label>Email Address</label><input value={contactDraft.store_email} onChange={(e) => setContactDraft({ ...contactDraft, store_email: e.target.value })} /></div>
              <div className="form-group"><label>City</label><input value={contactDraft.store_city} onChange={(e) => setContactDraft({ ...contactDraft, store_city: e.target.value })} /></div>
            </div>
            <div className="form-group"><label>Store Address</label><textarea rows="2" value={contactDraft.store_address} onChange={(e) => setContactDraft({ ...contactDraft, store_address: e.target.value })} /></div>

            <h5 className="sf-subheading">Social Links</h5>
            {contactDraft.social_links.map((link, index) => {
              const platformMeta = PLATFORM_OPTIONS.find(p => p.key === link.platform);
              return (
                <div key={index} className="sf-social-row">
                  <div className="sf-social-icon-chip" style={{ background: PLATFORM_COLORS[link.platform] || '#666' }}>
                    {platformMeta?.icon}
                  </div>
                  <select
                    value={link.platform}
                    onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                    className="sf-social-platform-select"
                  >
                    {PLATFORM_OPTIONS.map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                  <input
                    value={link.url}
                    onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                    placeholder="https://..."
                    className="sf-social-url-input"
                  />
                  <button type="button" className="sf-social-remove-btn" onClick={() => removeSocialLink(index)} title="Remove">
                    ✕
                  </button>
                </div>
              );
            })}

            <div className='social-btns'>
              <button type="button" className="sf-add-social-btn" onClick={addSocialLink}>
                + Add Social Link
              </button>

              <button className="sf-save-btn" onClick={saveContactSocial} disabled={saving}>Save Contact & Social Info</button>
            </div>


          </section>
        )}
      </div>

      {/* ===== DASHBOARD CONTENT ===== */}
      {/* 
      <div className="ss-card" ref={sectionRefs.dashboard}>
        <h2>Dashboard Content</h2>
        <p className="ss-sub">Text and links shown on your admin Dashboard.</p>

        <div className="ss-form-group">
          <label>Store Tip Text</label>
          <textarea
            rows="2"
            value={s('dashboard_store_tip')}
            onChange={(e) => setField('dashboard_store_tip', e.target.value)}
            placeholder="Products with clear photos, size options, and simple descriptions usually get more orders."
          />
        </div>

        <div className="ss-form-group">
          <label>Help Videos Link</label>
          <input
            type="text"
            value={s('help_videos_url')}
            onChange={(e) => setField('help_videos_url', e.target.value)}
            placeholder="e.g. a Google Drive folder link"
          />
          <small className="ss-hint">"Learn More" on the dashboard opens this link in a new tab. Leave blank to hide the button.</small>
        </div>

        <button
          className="ss-save-btn"
          disabled={saving === 'dashboard_content'}
          onClick={() => saveFields('dashboard_content', ['dashboard_store_tip', 'help_videos_url'], 'Dashboard content saved!')}
        >
          {saving === 'dashboard_content' ? 'Saving...' : 'Save Dashboard Content'}
        </button>
      </div>
      */}

      {/* ===== SHIPPING ===== */}
      <div className="ss-card" ref={sectionRefs.shipping}>
        <h2>Shipping</h2>
        <p className="ss-sub">Delivery charges shown to customers at checkout, based on their city.</p>
        <p className="ss-hint" style={{ marginBottom: 16 }}>
          Not yet connected to the live Cart/Checkout pages — saving here only stores the values for now.
        </p>

        <div className="ss-form-group">
          <label>Home City</label>
          <input
            type="text"
            value={s('shipping_home_city')}
            onChange={(e) => setField('shipping_home_city', e.target.value)}
            placeholder="e.g. Karachi"
          />
          <small className="ss-hint">Orders delivered within this city use the Home City rate below; everywhere else uses the Other City rate.</small>
        </div>

        <div className="ss-form-row">
          <div className="ss-form-group">
            <label>Home City Shipping Rate (PKR)</label>
            <input
              type="number"
              value={s('shipping_home_city_rate', '150')}
              onChange={(e) => setField('shipping_home_city_rate', e.target.value)}
            />
          </div>
          <div className="ss-form-group">
            <label>Other City Shipping Rate (PKR)</label>
            <input
              type="number"
              value={s('shipping_other_city_rate', '250')}
              onChange={(e) => setField('shipping_other_city_rate', e.target.value)}
            />
          </div>
        </div>

        <div className="ss-toggle-row">
          <div>
            <strong>Free Shipping Above a Spend Threshold</strong>
            <p>Automatically waive shipping (both rates above) once the cart total crosses this amount.</p>
          </div>
          <label className="ss-switch">
            <input type="checkbox" checked={isOn('free_shipping_enabled')} onChange={() => toggleField('free_shipping_enabled')} />
            <span className="ss-slider-track" />
          </label>
        </div>

        {isOn('free_shipping_enabled') && (
          <div className="ss-form-group">
            <label>Free Shipping Threshold (PKR)</label>
            <input
              type="number"
              value={s('free_shipping_threshold', '4000')}
              onChange={(e) => setField('free_shipping_threshold', e.target.value)}
            />
          </div>
        )}

        <button
          className="ss-save-btn"
          disabled={saving === 'shipping'}
          onClick={() => saveFields('shipping', ['shipping_home_city', 'shipping_home_city_rate', 'shipping_other_city_rate', 'free_shipping_enabled', 'free_shipping_threshold'], 'Shipping settings saved!')}
        >
          {saving === 'shipping' ? 'Saving...' : 'Save Shipping Settings'}
        </button>
      </div>

      {/* ===== DISCOUNT ===== */}
      <div className="ss-card" ref={sectionRefs.discount}>
        <h2>Promo Discount</h2>
        <p className="ss-sub">Configure a single promo code customers can enter at checkout. Optionally limit it to a date range.</p>

        <div className="ss-toggle-row">
          <div>
            <strong>Enable Promo Code</strong>
            <p>Turn this off to disable the code entirely, without deleting your settings.</p>
          </div>
          <label className="ss-switch">
            <input type="checkbox" checked={isOn('promo_code_enabled')} onChange={() => toggleField('promo_code_enabled')} />
            <span className="ss-slider-track" />
          </label>
        </div>

        {isOn('promo_code_enabled') && (
          <div className="ss-discount-detail-box">
            <div className="ss-form-group">
              <label>Promo Code</label>
              <input
                type="text"
                value={s('promo_code')}
                onChange={(e) => setField('promo_code', e.target.value.toUpperCase())}
                placeholder="e.g. WELCOME10"
              />
              <small className="ss-hint">Customers enter this exact code (not case-sensitive) at checkout.</small>
            </div>

            <div className="ss-form-group">
              <label>Discount Type</label>
              <select value={s('promo_discount_type', 'percentage')} onChange={(e) => setField('promo_discount_type', e.target.value)}>
                <option value="percentage">Percentage Discount</option>
                <option value="fixed_cart">Fixed Cart Discount</option>
                <option value="fixed_product">Fixed Product Discount</option>
              </select>
            </div>

            <div className="ss-form-group">
              <label>
                {s('promo_discount_type', 'percentage') === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (PKR)'}
              </label>
              <input
                type="number"
                value={s('promo_discount_value', '10')}
                onChange={(e) => setField('promo_discount_value', e.target.value)}
              />
            </div>

            {s('promo_discount_type', 'percentage') === 'fixed_product' && (
              <div className="ss-form-group">
                <label>Applicable Product ID</label>
                <input
                  type="number"
                  value={s('promo_product_id')}
                  onChange={(e) => setField('promo_product_id', e.target.value)}
                  placeholder="e.g. 24"
                />
                <small className="ss-hint">The discount only applies if this product is in the customer's cart. Find a product's ID from its edit page.</small>
              </div>
            )}

            <div className="ss-form-row">
              <div className="ss-form-group">
                <label>Start Date <span className="ss-optional">Optional</span></label>
                <input
                  type="date"
                  value={s('promo_code_start_date')}
                  onChange={(e) => setField('promo_code_start_date', e.target.value)}
                />
              </div>
              <div className="ss-form-group">
                <label>End Date <span className="ss-optional">Optional</span></label>
                <input
                  type="date"
                  value={s('promo_code_end_date')}
                  onChange={(e) => setField('promo_code_end_date', e.target.value)}
                />
              </div>
            </div>
            <small className="ss-hint">Leave both dates blank to run indefinitely until you turn this off.</small>
          </div>
        )}

        <button
          className="ss-save-btn"
          disabled={saving === 'discount'}
          onClick={() => saveFields('discount', [
            'promo_code_enabled', 'promo_code', 'promo_discount_type', 'promo_discount_value', 'promo_product_id',
            'promo_code_start_date', 'promo_code_end_date'
          ], 'Discount settings saved!')}
        >
          {saving === 'discount' ? 'Saving...' : 'Save Discount Settings'}
        </button>
      </div>

      {/* ===== FLAT DISCOUNT ===== */}
      <div className="ss-card" ref={sectionRefs.flat_discount}>
        <h2>Flat Discount</h2>
        <p className="ss-sub">A site-wide percentage off every product, applied automatically — no code needed. Optionally limited to a date range.</p>

        <div className="ss-toggle-row">
          <div>
            <strong>Enable Flat Discount</strong>
            <p>When on, this percentage is deducted automatically for every customer.</p>
          </div>
          <label className="ss-switch">
            <input type="checkbox" checked={isOn('flat_discount_enabled')} onChange={() => toggleField('flat_discount_enabled')} />
            <span className="ss-slider-track" />
          </label>
        </div>

        {isOn('flat_discount_enabled') && (
          <>
            <div className="ss-form-group">
              <label>Discount Percentage (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={s('flat_discount_percentage', '10')}
                onChange={(e) => setField('flat_discount_percentage', e.target.value)}
              />
            </div>

            <div className="ss-form-row">
              <div className="ss-form-group">
                <label>Start Date <span className="ss-optional">Optional</span></label>
                <input
                  type="date"
                  value={s('flat_discount_start_date')}
                  onChange={(e) => setField('flat_discount_start_date', e.target.value)}
                />
              </div>
              <div className="ss-form-group">
                <label>End Date <span className="ss-optional">Optional</span></label>
                <input
                  type="date"
                  value={s('flat_discount_end_date')}
                  onChange={(e) => setField('flat_discount_end_date', e.target.value)}
                />
              </div>
            </div>
            <small className="ss-hint">Leave both dates blank to run indefinitely until you turn this off.</small>
          </>
        )}

        <button
          className="ss-save-btn"
          disabled={saving === 'flat_discount'}
          onClick={() => saveFields('flat_discount', [
            'flat_discount_enabled', 'flat_discount_percentage', 'flat_discount_start_date', 'flat_discount_end_date'
          ], 'Flat discount settings saved!')}
        >
          {saving === 'flat_discount' ? 'Saving...' : 'Save Flat Discount Settings'}
        </button>
      </div>

      {/* ===== ACCOUNT ===== */}
      <div className="ss-card" ref={sectionRefs.account}>
        <h2>Account</h2>
        <p className="ss-sub">Your admin login credentials — changing these requires your current password.</p>

        {accountLoading ? (
          <p className="ss-hint">Loading account info...</p>
        ) : (
          <form onSubmit={handleAccountSave}>
            <div className="ss-form-row">
              <div className="ss-form-group">
                <label>Username</label>
                <input type="text" value={account.username} onChange={(e) => setAccount({ ...account, username: e.target.value })} required />
              </div>
              <div className="ss-form-group">
                <label>Email</label>
                <input type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} required />
              </div>
            </div>

            <div className="ss-form-group">
              <label>Current Password <span className="ss-required">(required to save any change below)</span></label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>

            <div className="ss-form-row">
              <div className="ss-form-group">
                <label>New Password <span className="ss-optional">Leave blank to keep current password</span></label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
              <div className="ss-form-group">
                <label>Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>

            <button type="submit" className="ss-save-btn" disabled={accountSaving}>
              {accountSaving ? 'Saving...' : 'Save Account'}
            </button>
          </form>
        )}
      </div>

      {/* ===== POLICY PAGES ===== */}
      {policyDraft && (
        <div className="ss-card" ref={sectionRefs.policies}>
          <h2>Policy Pages</h2>
          <p className="ss-sub">These policies appear on your storefront and in the system-generated footer.</p>

          <div className="ss-policy-tabs">
            {['shipping', 'returns', 'privacy', 'terms'].map(tab => (
              <button
                key={tab}
                type="button"
                className={`ss-policy-tab ${activePolicyTab === tab ? 'active' : ''}`}
                onClick={() => setActivePolicyTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="ss-form-group">
            <label>{activePolicyTab.charAt(0).toUpperCase() + activePolicyTab.slice(1)} Policy</label>
            <textarea
              rows="6"
              value={policyDraft[activePolicyTab]}
              onChange={(e) => setPolicyDraft({ ...policyDraft, [activePolicyTab]: e.target.value })}
            />
          </div>

          <button className="ss-save-btn" onClick={savePolicies} disabled={saving === 'policies'}>
            {saving === 'policies' ? 'Saving...' : 'Save Policies'}
          </button>
        </div>
      )}

      {/* ===== DANGER ZONE ===== */}
      <div className="ss-card ss-danger-card" ref={sectionRefs.danger}>
        <h2 className="ss-danger-title"><FaExclamationTriangle /> Danger Zone</h2>
        <p className="ss-sub">Actions here affect your live storefront immediately — proceed carefully.</p>

        <div className="ss-danger-row">
          <div>
            <strong>{s('maintenance_mode_enabled') === '1' ? 'Store is Paused' : 'Pause Store'}</strong>
            <p>
              {s('maintenance_mode_enabled') === '1'
                ? 'Your storefront currently shows a maintenance page to all visitors. Your admin dashboard remains accessible.'
                : 'Puts your live storefront into maintenance mode — visitors will see a "We\'ll be right back" page instead of your store. You can turn this off anytime.'}
            </p>
          </div>

          {s('maintenance_mode_enabled') === '1' ? (
            <button className="ss-resume-btn" onClick={handleTogglePauseStore} disabled={pauseSaving}>
              {pauseSaving ? 'Resuming...' : 'Resume Store'}
            </button>
          ) : (
            <button className="ss-pause-btn" onClick={handleTogglePauseStore} disabled={pauseSaving}>
              {pauseSaving ? 'Pausing...' : 'Pause Store'}
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default StoreSettings;